import { Flex, chakra, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import config from 'configs/app';
import useApiQuery from 'lib/api/useApiQuery';
import dayjs from 'lib/date/dayjs';
import { hasGasPriceValue, normalizeGasPriceInfo } from 'lib/gas/normalizeGasPrices';
import { useI18n } from 'lib/i18n/context';
import { HOMEPAGE_STATS } from 'stubs/stats';
import Skeleton from 'ui/shared/chakra/Skeleton';
import GasInfoTooltip from 'ui/shared/gas/GasInfoTooltip';
import GasPrice from 'ui/shared/gas/GasPrice';
import TextSeparator from 'ui/shared/TextSeparator';

function formatCoinPrice(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  return parsed.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
}

const TopBarStats = () => {
  const { data, isPlaceholderData, isError, refetch, dataUpdatedAt } = useApiQuery('stats', {
    queryOptions: {
      placeholderData: HOMEPAGE_STATS,
      refetchOnMount: false,
    },
  });

  React.useEffect(() => {
    if (isPlaceholderData || !data?.gas_price_updated_at) {
      return;
    }

    const endDate = dayjs(dataUpdatedAt).add(data.gas_prices_update_in, 'ms');
    const timeout = endDate.diff(dayjs(), 'ms');

    if (timeout <= 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      refetch();
    }, timeout);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [ isPlaceholderData, data?.gas_price_updated_at, dataUpdatedAt, data?.gas_prices_update_in, refetch ]);

  const { t } = useI18n();
  const valueColor = useColorModeValue('#000000', '#FFFFFF');
  const labelColor = useColorModeValue('#999999', '#616161');
  const separatorColor = useColorModeValue('#E9ECEF', '#2F2F2F');

  if (isError) {
    return <div/>;
  }

  const tokenLabel = config.chain.currency.symbol || config.chain.secondaryCoin.symbol || 'BOT';
  const coinPrice = formatCoinPrice(data?.coin_price);
  const averageGas = normalizeGasPriceInfo(data?.gas_prices?.average);
  const showGasPrice = config.features.gasTracker.isEnabled && hasGasPriceValue(averageGas);
  const showGasTooltip = config.features.gasTracker.isEnabled && Boolean(data?.gas_prices);
  const showCoinPrice = Boolean(coinPrice);

  const gasContent = (
    <Flex alignItems="center" gap={ 2 }>
      <chakra.span color={ labelColor }>{ t('topBar.gas') }</chakra.span>
      <chakra.span color={ valueColor } fontFamily="'Oxanium', var(--chakra-fonts-body)">
        { showGasPrice ? <GasPrice data={ averageGas }/> : '--' }
      </chakra.span>
    </Flex>
  );

  return (
    <Flex
      alignItems="center"
      fontSize="14px"
      fontWeight={ 500 }
      color={ valueColor }
      fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
      gap="16px"
      wrap="wrap"
    >
      { showCoinPrice && (
        <Flex alignItems="center" gap={ 2 }>
          <Skeleton isLoaded={ !isPlaceholderData }>
            <chakra.span color={ labelColor }>{ tokenLabel }</chakra.span>
          </Skeleton>
          <Skeleton isLoaded={ !isPlaceholderData }>
            <chakra.span fontFamily="'Oxanium', var(--chakra-fonts-body)">${ coinPrice }</chakra.span>
          </Skeleton>
        </Flex>
      ) }
      { showCoinPrice && <TextSeparator color={ separatorColor }/> }
      <Skeleton isLoaded={ !isPlaceholderData }>
        { showGasTooltip && data ? (
          <GasInfoTooltip data={ data } dataUpdatedAt={ dataUpdatedAt } placement="bottom-start">
            <Flex alignItems="center" cursor="pointer">
              { gasContent }
            </Flex>
          </GasInfoTooltip>
        ) : gasContent }
      </Skeleton>
    </Flex>
  );
};

export default React.memo(TopBarStats);
